import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, FileText, Search, UserCheck, Phone, MapPin } from 'lucide-react';

function SitemapPage() {
  const { t, i18n } = useTranslation();

  const sitemapSections = [
    {
      title: i18n.language === 'mr' ? 'मुख्य पानी' : 'Main Pages',
      icon: Home,
      links: [
        {
          path: '/',
          name: i18n.language === 'mr' ? 'मुख्यपृष्ठ' : 'Home Page',
          description:
            i18n.language === 'mr'
              ? 'वेबसाइटचे मुख्य पान'
              : 'Main homepage of the website',
        },
        {
          path: '/complaint/new',
          name:
            i18n.language === 'mr'
              ? 'नवीन तक्रार नोंदवा'
              : 'Register New Complaint',
          description:
            i18n.language === 'mr'
              ? 'नवीन नागरिक तक्रार सादर करा'
              : 'Submit a new citizen complaint',
        },
        {
          path: '/complaint/status',
          name:
            i18n.language === 'mr'
              ? 'तक्रारीची स्थिती पाहा'
              : 'Track Complaint Status',
          description:
            i18n.language === 'mr'
              ? 'आपल्या तक्रारीची सद्यस्थिती पाहा'
              : 'Check the current status of your complaint',
        },
      ],
    },
    {
      title: i18n.language === 'mr' ? 'अधिकारी पानी' : 'Officer Pages',
      icon: UserCheck,
      links: [
        {
          path: '/login',
          name: i18n.language === 'mr' ? 'अधिकारी लॉगिन' : 'Officer Login',
          description:
            i18n.language === 'mr'
              ? 'सरकारी अधिकाऱ्यांसाठी लॉगिन'
              : 'Login portal for government officers',
        },
        {
          path: '/dashboard',
          name:
            i18n.language === 'mr' ? 'अधिकारी डॅशबोर्ड' : 'Officer Dashboard',
          description:
            i18n.language === 'mr'
              ? 'तक्रारींचे व्यवस्थापन करा'
              : 'Manage and process citizen complaints',
        },
        {
          path: '/admin',
          name: i18n.language === 'mr' ? 'प्रशासकीय पॅनेल' : 'Admin Panel',
          description:
            i18n.language === 'mr'
              ? 'सिस्टम व्यवस्थापन'
              : 'System administration and management',
        },
      ],
    },
    {
      title: i18n.language === 'mr' ? 'माहिती पानी' : 'Information Pages',
      icon: FileText,
      links: [
        {
          path: '/sitemap',
          name: i18n.language === 'mr' ? 'साइट मॅप' : 'Site Map',
          description:
            i18n.language === 'mr'
              ? 'वेबसाइटच्या सर्व पानांची यादी'
              : 'Complete list of all website pages',
        },
        {
          path: '/accessibility',
          name: i18n.language === 'mr' ? 'सुलभ वापर सुविधा' : 'Accessibility',
          description:
            i18n.language === 'mr'
              ? 'वेबसाइट प्रवेशयोग्यतेची माहिती'
              : 'Website accessibility information',
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100">
      {/* Header */}
      <div
        className="w-full bg-white border-b-4 shadow-lg"
        style={{ borderBottomColor: '#B8860B' }}
      >
        <div
          className="w-full h-2"
          style={{
            background:
              'linear-gradient(to right, #FF6600 0%, #FFD700 50%, #FF6600 100%)',
          }}
        ></div>

        <div className="py-6 px-6">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {i18n.language === 'mr' ? 'साइट मॅप' : 'Site Map'}
            </h1>
            <p className="text-gray-600">
              {i18n.language === 'mr'
                ? 'गडचिरोली जिल्हा नागरिक तक्रार व्यवस्थापन प्रणाली - सर्व पानांची यादी'
                : 'Gadchiroli District Citizen Complaint Management System - Complete Page Directory'}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-8 lg:px-16">
        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-8">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <ol className="flex items-center space-x-2 text-sm text-gray-600">
              <li>
                <Link
                  to="/"
                  className="hover:text-gray-800 transition-colors"
                  style={{ color: '#B8860B' }}
                >
                  {i18n.language === 'mr' ? 'मुख्यपृष्ठ' : 'Home'}
                </Link>
              </li>
              <li>/</li>
              <li className="font-medium text-gray-800">
                {i18n.language === 'mr' ? 'साइट मॅप' : 'Site Map'}
              </li>
            </ol>
          </nav>

          {/* Sitemap Sections */}
          <div className="space-y-8">
            {sitemapSections.map((section, sectionIndex) => {
              const IconComponent = section.icon;
              return (
                <div
                  key={sectionIndex}
                  className="border border-gray-200 rounded-xl p-6 bg-gradient-to-r from-gray-50 to-white"
                >
                  <div className="flex items-center mb-4">
                    <IconComponent
                      className="w-6 h-6 mr-3"
                      style={{ color: '#B8860B' }}
                    />
                    <h2 className="text-xl font-bold text-gray-800">
                      {section.title}
                    </h2>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {section.links.map((link, linkIndex) => (
                      <div
                        key={linkIndex}
                        className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 hover:border-yellow-300"
                      >
                        <Link to={link.path} className="block group">
                          <h3 className="font-semibold text-gray-800 mb-2 group-hover:text-yellow-700 transition-colors">
                            {link.name}
                          </h3>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {link.description}
                          </p>
                          <div
                            className="mt-2 text-xs font-medium"
                            style={{ color: '#B8860B' }}
                          >
                            {link.path}
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Contact Information */}
          <div
            className="mt-12 p-6 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, #B8860B 0%, #DAA520 100%)',
            }}
          >
            <div className="text-center text-white">
              <h3 className="text-lg font-bold mb-2">
                {i18n.language === 'mr'
                  ? 'संपर्क माहिती'
                  : 'Contact Information'}
              </h3>
              <p className="text-yellow-100 mb-4">
                {i18n.language === 'mr'
                  ? 'तांत्रिक सहाय्य किंवा अधिक माहितीसाठी संपर्क करा'
                  : 'For technical support or more information, please contact us'}
              </p>

              <div className="flex flex-wrap justify-center items-center space-x-6 text-yellow-100">
                <div className="flex items-center">
                  <Phone className="w-4 h-4 mr-2" />
                  <span className="text-sm">1800-XXX-XXXX</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span className="text-sm">
                    {i18n.language === 'mr'
                      ? 'गडचिरोली, महाराष्ट्र'
                      : 'Gadchiroli, Maharashtra'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SitemapPage;
